import { motion, useScroll, useTransform } from 'framer-motion';
import { Calendar, Phone, Users, TrendingUp } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/ai-scale-hq/30min';

const highlights = [
  { icon: Phone, text: 'AI captures every missed call' },
  { icon: Users, text: 'Dedicated closer works your pipeline' },
  { icon: TrendingUp, text: 'More jobs booked from existing leads' },
];

const proofStats = [
  { value: '62%', label: 'of calls go unanswered' },
  { value: '85%', label: 'never call back' },
  { value: '21x', label: 'better odds in 5 min' },
];

export const HeroSection = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.3], [0, 100]);
  const opacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);

  return (
    <section
      className="relative min-h-screen flex items-center justify-center pt-24 pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div 
        className="relative z-10 max-w-5xl mx-auto px-6 text-center"
        style={{ y, opacity }}
      >
        {/* Badge with fade in */}
        <motion.div
          initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
        >
          <motion.span 
            className="w-2 h-2 rounded-full bg-[#32D583]"
            animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <span className="text-sm text-white/70">Revenue Partner for Home Service Businesses</span>
        </motion.div>

        {/* Headline with blur-to-sharp */}
        <motion.h1
          initial={{ opacity: 0, y: 30, filter: 'blur(20px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6"
          data-testid="hero-headline"
        >
          Stop Losing Jobs
          <br />
          <motion.span 
            className="text-[#6C4DFF]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            You Already Paid For
          </motion.span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto mb-8 leading-relaxed"
          data-testid="hero-subheadline"
        >
          ScaleHQ helps contractors answer every call, recover missed leads, 
          and assigns a dedicated appointment setter to work your pipeline 
          and turn more opportunities into booked revenue.
        </motion.p>

        {/* Highlights with stagger */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-wrap justify-center gap-3 mb-8"
        >
          {highlights.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 15, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
              whileHover={{ scale: 1.03, borderColor: 'rgba(108,77,255,0.3)' }}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/8 transition-colors"
            >
              <item.icon className="w-4 h-4 text-[#6C4DFF]" />
              <span className="text-sm text-white/70">{item.text}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA with glow pulse */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mb-10"
        >
          <motion.a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            animate={{
              boxShadow: [
                '0 0 20px rgba(108,77,255,0.3)',
                '0 0 35px rgba(108,77,255,0.5)',
                '0 0 20px rgba(108,77,255,0.3)',
              ]
            }}
            transition={{ 
              boxShadow: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' }
            }}
            data-testid="hero-cta-primary"
          >
            <Calendar className="w-5 h-5" />
            Book a Strategy Call
          </motion.a>
        </motion.div>

        {/* Proof Stats with count-up animation feel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9 }}
          className="flex flex-wrap justify-center gap-8"
        >
          {proofStats.map((stat, index) => (
            <motion.div 
              key={index} 
              className="text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 1 + index * 0.15 }}
            >
              <motion.div 
                className="text-2xl font-bold text-white"
                animate={{ 
                  textShadow: ['0 0 0 rgba(255,255,255,0)', '0 0 10px rgba(255,255,255,0.2)', '0 0 0 rgba(255,255,255,0)']
                }}
                transition={{ duration: 2, delay: 1.5 + index * 0.3, repeat: Infinity, repeatDelay: 4 }}
              >
                {stat.value}
              </motion.div>
              <div className="text-xs text-white/50">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Industries */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.3 }}
          className="text-sm text-white/40 mt-8"
        >
          For Plumbing, Electrical, Roofing, Remodeling & Home Services
        </motion.p>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2"
        >
          <motion.div 
            className="w-1 h-2 bg-white/40 rounded-full"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
